import { FieldWrapper, getFieldErrorMessage } from "@birdwatch/ui/form";
import type { ComponentProps, ReactNode } from "react";
import { NumberInput } from "@/components/ui/input/number-input";
import {
  type FieldBindingProps,
  PolyField,
  splitBinding,
} from "@/fields/_internal/poly-field";

type NumberFieldBaseProps = Omit<
  ComponentProps<typeof NumberInput>,
  "value" | "onChange" | "onBlur"
> & {
  label?: ReactNode;
  description?: ReactNode;
  optional?: boolean;
  onBlur?: () => void;
  onValueChange?: (value: number) => void;
};

export type NumberFieldProps = NumberFieldBaseProps & FieldBindingProps;

export function NumberField(props: NumberFieldProps) {
  const [binding, rest] = splitBinding(props);
  const {
    label,
    description,
    optional,
    className,
    onBlur,
    onValueChange,
    ...inputProps
  } = rest;
  return (
    <PolyField<number> {...binding}>
      {(field) => (
        <FieldWrapper
          label={label}
          description={description}
          optional={optional}
          error={getFieldErrorMessage(field)}
          className={className}
          disabled={inputProps.disabled}
        >
          <NumberInput
            {...inputProps}
            value={field.state.value ?? 0}
            onChange={(value) => {
              // NumberInput always emits a number — its prop type only widens
              // to ChangeEvent because it intersects Input's own onChange.
              const next = value as number;
              field.handleChange(next);
              onValueChange?.(next);
            }}
            onBlur={() => {
              field.handleBlur();
              onBlur?.();
            }}
          />
        </FieldWrapper>
      )}
    </PolyField>
  );
}
